import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useJoinRoom } from "@/hooks/use-room";
import { applyServerErrors, type SetFieldError } from "@/lib/form-errors";
import { lastPlayerName, rememberPlayerName } from "@/lib/guest";

const schema = z.object({
  name: z.string().trim().min(1, "Enter your name").max(40, "Keep it under 40 characters"),
});

export function JoinForm({ roomId, roomName }: { roomId: string; roomName: string }) {
  const join = useJoinRoom(roomId);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: lastPlayerName() },
  });

  const onSubmit = form.handleSubmit(async ({ name }) => {
    try {
      await join.mutateAsync(name);
      rememberPlayerName(name);
    } catch (error) {
      const message = applyServerErrors(error, form.setError as SetFieldError);
      if (message) form.setError("root", { message });
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm border-border/50">
        <CardHeader className="text-center">
          <Logo className="mx-auto mb-2 size-8" />
          <CardTitle className="text-xl">Join Room</CardTitle>
          <p className="text-sm text-muted-foreground" data-testid="join-room-name">
            {roomName}
          </p>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="join-form">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your display name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your name" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.formState.errors.root && (
                <p role="alert" className="text-sm text-destructive">
                  {form.formState.errors.root.message}
                </p>
              )}
              <Button type="submit" disabled={join.isPending} className="w-full">
                {join.isPending ? "Joining..." : "Join Game"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
